import { inject, Injectable, InjectionToken } from '@angular/core';
import { LocaleService } from '@next/core-lib/i18n';
import { Observable, catchError, map, of, startWith } from 'rxjs';
import { DataPlatformApiService } from 'src/app/tga/shared/services/data-platform-api.service';
import {
  BiomassBenchmarkEvaluationDatasourceState,
  BiomassBenchmarkEvaluationRequest,
  BiomassBenchmarkDatasourceState,
  BiomassBenchmarkRequest,
} from './biomass-benchmark.models';

export interface BiomassBenchmarkDatasource {
  loadData(request: BiomassBenchmarkRequest): Observable<BiomassBenchmarkDatasourceState>;
}

@Injectable({
  providedIn: 'root',
})
export class ApiBiomassBenchmarkDatasource implements BiomassBenchmarkDatasource {
  private readonly dataPlatformApiService = inject(DataPlatformApiService);

  loadData(request: BiomassBenchmarkRequest): Observable<BiomassBenchmarkDatasourceState> {
    if (request.cropFieldId <= 0 || request.cropVarietyId <= 0) {
      return of({
        state: 'error',
        messageKey: 'TGA.Advice.Cultivation.BiomassBenchmark.Error',
      });
    }

    return this.dataPlatformApiService
      .getBiomassBenchmark(request.cropFieldId, request.cropYear, request.cropVarietyId)
      .pipe(
        map((response): BiomassBenchmarkDatasourceState => {
          const hasFieldPoints = response.points.some((point) => point.value !== null);
          const hasBenchmarkPoints = response.comparableCropFields.some((series) =>
            series.points.some((point) => point.value !== null),
          );

          if (!hasFieldPoints && !hasBenchmarkPoints) {
            return { state: 'empty' };
          }

          return {
            state: 'loaded',
            data: {
              points: response.points,
              comparableCropFields: response.comparableCropFields,
            },
          };
        }),
        catchError(() =>
          of<BiomassBenchmarkDatasourceState>({
            state: 'error',
            messageKey: 'TGA.Advice.Cultivation.BiomassBenchmark.Error',
          }),
        ),
      );
  }
}

export interface BiomassBenchmarkEvaluationDatasource {
  loadData(request: BiomassBenchmarkEvaluationRequest): Observable<BiomassBenchmarkEvaluationDatasourceState>;
}

@Injectable({
  providedIn: 'root',
})
export class ApiBiomassBenchmarkEvaluationDatasource implements BiomassBenchmarkEvaluationDatasource {
  private readonly dataPlatformApiService = inject(DataPlatformApiService);
  private readonly localeService = inject(LocaleService);

  loadData(request: BiomassBenchmarkEvaluationRequest): Observable<BiomassBenchmarkEvaluationDatasourceState> {
    if (request.cropFields.length === 0) {
      return of({ state: 'empty' });
    }

    return this.dataPlatformApiService
      .getBiomassBenchmarkEvaluation(
        request.cropYear,
        request.cropFields.map(({ cropFieldId, cropVarietyId }) => ({ cropFieldId, cropVarietyId })),
      )
      .pipe(
        map((response): BiomassBenchmarkEvaluationDatasourceState => {
          const fieldContexts = new Map(
            request.cropFields.map((cropField) => [cropField.cropFieldId.toString(), cropField]),
          );
          const varietyContexts = new Map(request.cropFields.map((cropField) => [cropField.cropVarietyId, cropField]));
          const fieldSeries = response.cropFields
            .map((series) => {
              const context = fieldContexts.get(series.cropFieldId);
              if (!context) {
                return null;
              }

              return {
                name: this.createFieldSeriesName(context),
                points: series.points,
              };
            })
            .filter(
              (series): series is { name: string; points: (typeof response.cropFields)[number]['points'] } =>
                series !== null,
            );
          const benchmarkSeries = response.benchmarks
            .map((series) => {
              const context = varietyContexts.get(series.cropVarietyId);
              if (!context) {
                return null;
              }

              return {
                name: this.createBenchmarkSeriesName(context.cropVarietyName),
                points: series.points,
              };
            })
            .filter(
              (series): series is { name: string; points: (typeof response.benchmarks)[number]['points'] } =>
                series !== null,
            );
          const hasFieldPoints = fieldSeries.some((series) => series.points.some((point) => point.value !== null));
          const hasBenchmarkPoints = benchmarkSeries.some((series) =>
            series.points.some((point) => point.value !== null),
          );

          if (!hasFieldPoints && !hasBenchmarkPoints) {
            return { state: 'empty' };
          }

          return {
            state: 'loaded',
            data: {
              points: [],
              fieldSeries,
              benchmarkSeries,
            },
          };
        }),
        startWith<BiomassBenchmarkEvaluationDatasourceState>({ state: 'loading' }),
        catchError(() =>
          of<BiomassBenchmarkEvaluationDatasourceState>({
            state: 'error',
            messageKey: 'TGA.Advice.Cultivation.BiomassBenchmark.Error',
          }),
        ),
      );
  }

  private createFieldSeriesName(context: BiomassBenchmarkEvaluationRequest['cropFields'][number]): string {
    const fieldName = context.cropFieldName.trim();
    const varietyName = context.cropVarietyName.trim();
    const name = varietyName ? `${fieldName} (${varietyName})` : fieldName;

    return this.localeService.translate('TGA.Advice.Cultivation.BiomassBenchmark.FieldSeriesName', {
      cropFieldName: name,
    });
  }

  private createBenchmarkSeriesName(cropVarietyName: string): string {
    return this.localeService.translate('TGA.Advice.Cultivation.BiomassBenchmark.BenchmarkSeriesNameWithVariety', {
      cropVarietyName: cropVarietyName.trim(),
    });
  }
}

export const BIOMASS_BENCHMARK_DATASOURCE = new InjectionToken<BiomassBenchmarkDatasource>(
  'BIOMASS_BENCHMARK_DATASOURCE',
  {
    providedIn: 'root',
    factory: () => inject(ApiBiomassBenchmarkDatasource),
  },
);

export const BIOMASS_BENCHMARK_EVALUATION_DATASOURCE = new InjectionToken<BiomassBenchmarkEvaluationDatasource>(
  'BIOMASS_BENCHMARK_EVALUATION_DATASOURCE',
  {
    providedIn: 'root',
    factory: () => inject(ApiBiomassBenchmarkEvaluationDatasource),
  },
);
